/**
 * marketplaceRequestList store — the pending marketplace-requests LIST
 * (`GET /api/marketplace/pro/requests`) as one module-level cache (plan 443).
 *
 * The header's Home badge and the Home page both need the rows (the Needs
 * stream is built from them), and the Today store merges a count/top-fee
 * summary of the same list onto its queue. They used to fetch it separately —
 * per hook instance, plus once more in the Today store on every
 * `bumpMarketplaceRequests` — so the badge and the list could briefly disagree.
 * Now one request is in flight at a time and every reader shares its result.
 *
 * Follows the idiom of `stores/proHome.ts`: a zustand store plus a module-level
 * in-flight promise.
 *
 *   - A mounted consumer reports `enabled` (the go-live gate is `on`). With no
 *     enabled consumer the store issues no request of its own: gate `off` or
 *     `loading` means ZERO marketplace calls. (`loadRequestRows` is the one
 *     exception, for the Today store's merged summary, which has its own
 *     gate.)
 *   - `bumpMarketplaceRequests` refetches once while a consumer is mounted;
 *     otherwise it marks the cache stale and the next mount refetches. A bump
 *     that lands during a fetch of an older version chains exactly one
 *     follow-up.
 *   - IS_PREVIEW serves the Home fixture's pending requests, never the network.
 *
 * `rows: null` = nothing loaded or the last fetch failed; the needs stream
 * treats it as "no requests".
 */
import { create } from "zustand";
import { listMarketplaceProRequests, type MarketplaceConnectionRequest } from "../lib/api";
import { loadPreviewHomeFixture, previewRequestsFor } from "../lib/pro-home-preview";
import { IS_PREVIEW } from "../lib/pro-mode";
import type { GetProToken } from "../hooks/useProToken";
import { useMarketplaceRequestsRefreshStore } from "./marketplaceRequests";

/** A cache older than this is refetched when a consumer attaches. */
export const REQUEST_LIST_STALE_MS = 60_000;

export interface RequestListConsumer {
  getToken: GetProToken;
}

interface RequestListState {
  rows: MarketplaceConnectionRequest[] | null;
  /**
   * The `bumpMarketplaceRequests` version whose fetch last resolved or failed
   * (null = none yet). A reader is settled when this equals the current
   * version.
   */
  loadedVersion: number | null;
  /** `Date.now()` of the last successful fetch. */
  fetchedAt: number | null;
  /** Invalidated, or the last fetch failed: the next attach refetches. */
  stale: boolean;
  /** Register a mounted, enabled consumer; returns its detach. Loads if due. */
  attach: (consumer: RequestListConsumer) => () => void;
  reset: () => void;
}

const consumers = new Set<RequestListConsumer>();
let inFlight: { promise: Promise<MarketplaceConnectionRequest[] | null>; version: number } | null =
  null;
// Bumped by reset() so a fetch that outlives it cannot write into fresh state.
let generation = 0;

function currentVersion(): number {
  return useMarketplaceRequestsRefreshStore.getState().version;
}

function anyToken(): GetProToken {
  const [first] = consumers;
  return first ? first.getToken : () => Promise.resolve(null);
}

function startLoad(getToken: GetProToken): Promise<MarketplaceConnectionRequest[] | null> {
  const gen = generation;
  const version = currentVersion();
  const run = (async () => {
    try {
      const rows = IS_PREVIEW
        ? previewRequestsFor((await loadPreviewHomeFixture()).home)
        : await listMarketplaceProRequests((await getToken()) ?? undefined);
      if (gen === generation) {
        useMarketplaceRequestListStore.setState({
          rows,
          loadedVersion: version,
          fetchedAt: Date.now(),
          stale: false,
        });
      }
      return rows;
    } catch {
      // Best-effort: a failed fetch leaves the badge without request rows.
      if (gen === generation) {
        useMarketplaceRequestListStore.setState({
          rows: null,
          loadedVersion: version,
          stale: true,
        });
      }
      return null;
    }
  })();
  const entry = {
    version,
    promise: run.finally(() => {
      if (inFlight === entry) inFlight = null;
    }),
  };
  inFlight = entry;
  return entry.promise;
}

/**
 * The pending requests, sharing the one in-flight read. Resolves `null` when
 * the read failed. A read for an older bump version is awaited and followed by
 * one fresh read. With `maxAgeMs`, a cache loaded for the current version no
 * older than that is returned without a request.
 */
export function loadRequestRows(
  getToken: GetProToken,
  opts: { maxAgeMs?: number } = {},
): Promise<MarketplaceConnectionRequest[] | null> {
  if (inFlight) {
    if (inFlight.version === currentVersion()) return inFlight.promise;
    return inFlight.promise.then(() => loadRequestRows(getToken, opts));
  }
  const { rows, loadedVersion, fetchedAt, stale } = useMarketplaceRequestListStore.getState();
  if (
    opts.maxAgeMs !== undefined &&
    !stale &&
    rows !== null &&
    loadedVersion === currentVersion() &&
    fetchedAt !== null &&
    Date.now() - fetchedAt <= opts.maxAgeMs
  ) {
    return Promise.resolve(rows);
  }
  return startLoad(getToken);
}

export const useMarketplaceRequestListStore = create<RequestListState>((set, get) => ({
  rows: null,
  loadedVersion: null,
  fetchedAt: null,
  stale: false,

  attach: (consumer) => {
    consumers.add(consumer);
    const { loadedVersion, fetchedAt, stale } = get();
    const due =
      stale ||
      loadedVersion !== currentVersion() ||
      fetchedAt === null ||
      Date.now() - fetchedAt > REQUEST_LIST_STALE_MS;
    if (due) void loadRequestRows(consumer.getToken);
    return () => {
      consumers.delete(consumer);
    };
  },

  reset: () => {
    generation += 1;
    consumers.clear();
    inFlight = null;
    set({ rows: null, loadedVersion: null, fetchedAt: null, stale: false });
  },
}));

// A confirm/decline elsewhere changes the pending list. One refetch while a
// consumer is mounted (joined or chained by `loadRequestRows`, so the Today
// store's own reaction to the same bump shares it); otherwise just stale.
useMarketplaceRequestsRefreshStore.subscribe((state, prev) => {
  if (state.version === prev.version) return;
  if (consumers.size > 0) void loadRequestRows(anyToken());
  else useMarketplaceRequestListStore.setState({ stale: true });
});
